import { Link } from 'react-router-dom';
import FurnitureNavbar from '../../../components/layout/Nav';
import './admindashboard.scss';
import { useEffect, useState } from 'react';
import withAuth from '../../auth/WithAuth';
import { serverUrlAuth } from '../../../serverUrl';

const StatCard = ({ title, value, change, icon, color, link }) => (
  <div className={`stat-card ${color}`}>
    <div className="stat-content">
      <div className="stat-icon">{icon}</div>
      <div className="stat-info">
        <h3>{title}</h3>
        <p className="stat-value">{value}</p>
        {change && (
          <span className={`stat-change ${change.type}`}>{change.value}</span>
        )}
      </div>
    </div>
    {link && (
      <Link to={link} className="stat-link">
        View Details →
      </Link>
    )}
  </div>
);

const RecentActivityItem = ({ type, message, time, user, status }) => (
  <div className="activity-item">
    <div className="activity-icon">
      {type === 'user' && '👤'}
      {type === 'project' && '📋'}
      {type === 'payment' && '💰'}
      {type === 'system' && '⚙️'}
    </div>
    <div className="activity-content">
      <p className="activity-message">{message}</p>
      <div className="activity-meta">
        <span className="activity-user">{user}</span>
        <span className="activity-time">{time}</span>
        {status && (
          <span className={`activity-status ${status}`}>{status}</span>
        )}
      </div>
    </div>
  </div>
);

const QuickAction = ({ icon, title, description, link, color }) => (
  <Link to={link} className={`quick-action ${color}`}>
    <div className="action-icon">{icon}</div>
    <div className="action-content">
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
    <div className="action-arrow">→</div>
  </Link>
);

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    try {
      const response = await serverUrlAuth.get('projects/admin/dashboard/');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching admin dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Mock data - replace with actual API data

  const recentActivities = [
    {
      id: 1,
      type: 'user',
      message: 'New artisan registration - John Doe',
      time: '5 min ago',
      user: 'System',
      status: 'pending',
    },
    {
      id: 2,
      type: 'project',
      message: 'Project "Custom Dining Table" completed',
      time: '1 hour ago',
      user: 'Sarah Kimani',
      status: 'completed',
    },
    {
      id: 3,
      type: 'payment',
      message: 'Payment dispute reported',
      time: '2 hours ago',
      user: 'Mike Johnson',
      status: 'warning',
    },
    {
      id: 4,
      type: 'system',
      message: 'System backup completed successfully',
      time: '3 hours ago',
      user: 'System',
      status: 'success',
    },
  ];

  const quickActions = [
    {
      icon: '👤',
      title: 'User Management',
      description: 'Manage users, artisans, and permissions',
      link: 'users-management',
      color: 'blue',
    },
    {
      icon: '📋',
      title: 'Project Moderation',
      description: 'Review and approve projects',
      link: '/projects-management',
      color: 'green',
    },
    {
      icon: '💰',
      title: 'Financial Overview',
      description: 'View transactions and revenue',
      link: '/admin/finance',
      color: 'purple',
    },
    {
      icon: '📊',
      title: 'Analytics',
      description: 'Platform insights and reports',
      link: '/admin/analytics',
      color: 'orange',
    },
  ];

  return (
    <>
      <FurnitureNavbar />
      <div className="admin-dashboard">
        {/* Header */}
        <header className="admin-header">
          <div className="container">
            <div className="header-content">
              <div className="header-text">
                <h1>Admin Dashboard 🛠️</h1>
                <p>Manage your platform and monitor performance</p>
              </div>
              <div className="header-actions">
                <button className="btn btn-primary">Generate Report</button>
                <button className="btn btn-secondary">Settings</button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="container">
          {/* Stats Overview */}
          <section className="stats-overview">
            <div className="section-header">
              <h2>Platform Overview</h2>
              <span className="last-updated">Updated just now</span>
            </div>
            <div className="stats-grid">
              <StatCard
                title="Total Users"
                value={dashboardData.total_users}
                icon="👥"
                color="blue"
                // link="/admin/users"
              />
              <StatCard
                title="Inactive Artisans"
                value={dashboardData.inactive_users}
                icon="👥"
                color="blue"
                // link="/admin/users"
              />
              <StatCard
                title="Artisans"
                value={dashboardData.artisans}
                icon="🔨"
                color="green"
                // link="/admin/artisans"
              />
              <StatCard
                title="Clients"
                value={dashboardData.clients}
                icon="💼"
                color="purple"
                // link="/admin/clients"
              />
              <StatCard
                title="Total Projects"
                value={dashboardData.total_projects}
                icon="📋"
                color="orange"
                // link="/admin/projects"
              />
               <StatCard
                title="Pending Projects"
                value={dashboardData.open_projects}
                icon="⏳"
                color="yellow"
                // link="/admin/approvals"
              />
              <StatCard
                title="Active Projects"
                value={dashboardData.ongoing_projects}
                icon="⚡"
                color="red"
                // link="/admin/active-projects"
              />
             
              <StatCard
                title="Completed Projects"
                value={dashboardData.completed_projects}
                icon="✅"
                color="yellow"
                // link="/admin/approvals"
              />
            </div>
          </section>

          {/* Quick Actions & Recent Activity */}
          <div className="dashboard-main">
            {/* Quick Actions */}
            <section className="quick-actions-section">
              <div className="section-header">
                <h2>Quick Actions</h2>
                <span>Frequently used admin tasks</span>
              </div>
              <div className="quick-actions-grid">
                {quickActions.map((action, index) => (
                  <QuickAction key={index} {...action} />
                ))}
              </div>
            </section>

            {/* Recent Activity */}
            <section className="recent-activity-section">
              <div className="section-header">
                <h2>Recent Activity</h2>
                <Link to="/admin/activity" className="view-all">
                  View All
                </Link>
              </div>
              <div className="activity-list">
                {recentActivities.map((activity) => (
                  <RecentActivityItem key={activity.id} {...activity} />
                ))}
              </div>
            </section>
          </div>

          {/* Additional Sections */}
          <div className="dashboard-secondary">
            {/* System Health */}
            <section className="system-health">
              <div className="section-header">
                <h2>System Health</h2>
                <span className="status-indicator online">
                  All Systems Operational
                </span>
              </div>
              <div className="health-metrics">
                <div className="health-metric">
                  <span className="metric-label">Server Uptime</span>
                  <div className="metric-bar">
                    <div
                      className="metric-fill"
                      style={{ width: '99.9%' }}
                    ></div>
                  </div>
                  <span className="metric-value">99.9%</span>
                </div>
                <div className="health-metric">
                  <span className="metric-label">Database</span>
                  <div className="metric-bar">
                    <div
                      className="metric-fill"
                      style={{ width: '100%' }}
                    ></div>
                  </div>
                  <span className="metric-value">100%</span>
                </div>
                <div className="health-metric">
                  <span className="metric-label">API Response</span>
                  <div className="metric-bar">
                    <div className="metric-fill" style={{ width: '98%' }}></div>
                  </div>
                  <span className="metric-value">98%</span>
                </div>
              </div>
            </section>

            {/* Recent Registrations */}
            <section className="recent-registrations">
              <div className="section-header">
                <h2>Recent Registrations</h2>
                <Link to="/admin/registrations" className="view-all">
                  View All
                </Link>
              </div>
              <div className="registrations-list">
                <div className="registration-item">
                  <div className="user-avatar">JD</div>
                  <div className="user-info">
                    <h4>John Doe</h4>
                    <p>Artisan • Woodworking</p>
                  </div>
                  <span className="registration-time">2 min ago</span>
                </div>
                <div className="registration-item">
                  <div className="user-avatar">SJ</div>
                  <div className="user-info">
                    <h4>Sarah Johnson</h4>
                    <p>Client • Interior Design</p>
                  </div>
                  <span className="registration-time">15 min ago</span>
                </div>
                <div className="registration-item">
                  <div className="user-avatar">MK</div>
                  <div className="user-info">
                    <h4>Mike Kimani</h4>
                    <p>Artisan • Metalwork</p>
                  </div>
                  <span className="registration-time">1 hour ago</span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuth(AdminDashboard, 'admin');
